/* Video testimonials (sections/video-testimonials.liquid)
 *
 * Each card starts as a poster. Pressing play moves the <video> out of its
 * <template> (so base.js never sees it and nothing downloads early), plays it,
 * and fades the poster once a frame is on screen. One video plays at a time
 * across the page, and a video pauses when it scrolls out of view.
 *
 * Mute is shared by every card in the section: unmute one and the next one
 * you play has sound too.
 */
(() => {
  if (customElements.get('video-testimonials')) return;

  const playing = new Set();

  class VideoTestimonials extends HTMLElement {
    connectedCallback() {
      this.muted = this.dataset.startMuted === 'true';
      this.track = this.querySelector('[data-vt-track]');
      this.nav = this.querySelector('[data-vt-nav]');
      this.prev = this.querySelector('[data-vt-prev]');
      this.next = this.querySelector('[data-vt-next]');
      this.cards = Array.from(this.querySelectorAll('[data-vt-card]'));

      this.cards.forEach((card) => {
        this.renderMuted(card);
        card.querySelectorAll('[data-vt-toggle]').forEach((button) => {
          button.addEventListener('click', () => this.toggle(card));
        });
        card.querySelector('[data-vt-mute]')?.addEventListener('click', () => {
          this.muted = !this.muted;
          this.cards.forEach((c) => this.renderMuted(c));
        });
      });

      this.visibility = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) entry.target.querySelector('video')?.pause();
          });
        },
        { threshold: 0.25 }
      );
      this.cards.forEach((card) => this.visibility.observe(card));

      this.prev?.addEventListener('click', () => this.page(-1));
      this.next?.addEventListener('click', () => this.page(1));
      this.onScroll = () => this.updateNav();
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.resize = new ResizeObserver(() => this.updateNav());
      this.resize.observe(this.track);

      this.onBlockSelect = (event) => {
        const item = event.target.closest('.vt__item');
        if (item && this.contains(item)) this.scrollToItem(item);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      this.cards.forEach((card) => {
        const video = card.querySelector('video');
        if (video) {
          video.pause();
          playing.delete(video);
        }
      });
      this.visibility?.disconnect();
      this.resize?.disconnect();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    /* Playback ------------------------------------------------------------ */

    toggle(card) {
      const video = card.querySelector('video');
      if (video && !video.paused) {
        video.pause();
      } else {
        this.play(card);
      }
    }

    play(card) {
      const video = card.querySelector('video') || this.mount(card);
      if (!video) return;

      playing.forEach((other) => other !== video && other.pause());

      video.muted = this.muted;
      if (card.dataset.state === 'idle') card.dataset.state = 'loading';

      video.play().catch(() => {
        // A browser that blocks sound (low-power mode, autoplay policy) still
        // allows muted playback; fall back rather than doing nothing.
        if (video.muted) return this.setState(card, video.currentTime > 0 ? 'paused' : 'idle');
        this.muted = true;
        this.cards.forEach((c) => this.renderMuted(c));
        video.muted = true;
        video.play().catch(() => this.setState(card, 'idle'));
      });
    }

    mount(card) {
      const template = card.querySelector('template[data-vt-video]');
      if (!template) return null;

      const video = template.content.querySelector('video').cloneNode(true);
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.preload = 'auto';
      video.removeAttribute('poster');
      template.replaceWith(video);

      video.addEventListener('playing', () => {
        playing.add(video);
        this.setState(card, 'playing');
      });
      video.addEventListener('pause', () => {
        playing.delete(video);
        if (!video.ended) this.setState(card, 'paused');
      });
      video.addEventListener('waiting', () => {
        if (card.dataset.state === 'idle') this.setState(card, 'loading');
      });
      video.addEventListener('ended', () => {
        playing.delete(video);
        video.currentTime = 0;
        this.setState(card, 'idle');
      });

      return video;
    }

    setState(card, state) {
      card.dataset.state = state;
      const button = card.querySelector('.vt__button[data-vt-toggle]');
      if (button) {
        button.setAttribute('aria-label', state === 'playing' ? button.dataset.labelPause : button.dataset.labelPlay);
      }
    }

    renderMuted(card) {
      card.toggleAttribute('data-muted', this.muted);
      const video = card.querySelector('video');
      if (video) video.muted = this.muted;

      const button = card.querySelector('[data-vt-mute]');
      if (!button) return;
      button.setAttribute('aria-pressed', String(this.muted));
      button.setAttribute('aria-label', this.muted ? button.dataset.labelUnmute : button.dataset.labelMute);
    }

    /* Carousel ------------------------------------------------------------ */

    step() {
      const item = this.track.querySelector('.vt__item');
      if (!item) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const perView = Math.max(1, Math.floor((this.track.clientWidth + gap) / (item.offsetWidth + gap)));
      return perView * (item.offsetWidth + gap);
    }

    page(direction) {
      this.track.scrollBy({ left: direction * this.step() });
    }

    scrollToItem(item) {
      this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft });
    }

    updateNav() {
      if (!this.nav) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.nav.hidden = max <= 1;
      this.prev.disabled = this.track.scrollLeft <= 1;
      this.next.disabled = this.track.scrollLeft >= max - 1;
    }
  }

  customElements.define('video-testimonials', VideoTestimonials);
})();
